import { useRef, useEffect, useCallback } from "react";
import { useTheme } from "../contexts/ThemeContext";

declare global {
  interface Window {
    turnstile: any;
    onloadTurnstileCallback?: () => void;
  }
}

// Environment check
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000XX";

interface UseTurnstileOptions {
  onSuccess?: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
}

export const useTurnstile = (options: UseTurnstileOptions = {}) => {
  const { theme } = useTheme();
  const { onSuccess, onError, onExpire } = options;
  
  // Turnstile references
  const turnstileRef = useRef<string | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const turnstileScriptLoaded = useRef<boolean>(false);

  // Initialize Turnstile widget
  const initializeTurnstile = useCallback(() => {
    if (turnstileContainer.current && window.turnstile) {
      // If a widget already exists, remove it first
      if (turnstileWidgetId.current) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (error) {
          console.warn("Failed to remove existing Turnstile widget:", error);
        }
      }

      // Create new widget
      turnstileWidgetId.current = window.turnstile.render(
        turnstileContainer.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: theme === "light" ? "light" : "dark",
          callback: (token: string) => {
            turnstileRef.current = token;
            onSuccess?.(token);
          },
          "error-callback": () => {
            turnstileRef.current = null;
            onError?.();
          },
          "expired-callback": () => {
            turnstileRef.current = null;
            onExpire?.();
          },
        }
      );
    }
  }, [theme, onSuccess, onError, onExpire]);

  // Load Turnstile script
  useEffect(() => {
    // Skip loading Turnstile in development
    if (IS_DEVELOPMENT || turnstileScriptLoaded.current) return;

    // Mark script as loaded to prevent duplicate loading
    turnstileScriptLoaded.current = true;

    // Create the script element
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
    script.async = true;
    script.defer = true;
    script.id = "turnstile-script";

    // Set up the callback
    window.onloadTurnstileCallback = () => {
      initializeTurnstile();
    };

    // Add script to body
    document.body.appendChild(script);

    // Cleanup function
    return () => {
      if (document.getElementById("turnstile-script")) {
        try {
          document.body.removeChild(script);
        } catch (error) {
          console.warn("Failed to remove Turnstile script:", error);
        }
      }
      // Clean up the callback when unmounting
      if (window.onloadTurnstileCallback) {
        delete window.onloadTurnstileCallback;
      }
      // Remove widget if it exists
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (error) {
          console.warn("Failed to remove Turnstile widget:", error);
        }
      }
    };
  }, [initializeTurnstile]);

  // Effect to handle theme changes
  useEffect(() => {
    if (!IS_DEVELOPMENT && turnstileScriptLoaded.current && window.turnstile) {
      initializeTurnstile();
    }
  }, [theme, initializeTurnstile]);

  // Reset Turnstile widget
  const resetTurnstile = useCallback(() => {
    if (!IS_DEVELOPMENT && turnstileWidgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileRef.current = null;
      } catch (error) {
        console.warn("Failed to reset Turnstile widget:", error);
        // Re-initialize the widget if reset fails
        setTimeout(() => {
          initializeTurnstile();
        }, 100);
      }
    }
  }, [initializeTurnstile]);

  // Get current token
  const getToken = useCallback(() => {
    return IS_DEVELOPMENT ? "dev_verification_token" : turnstileRef.current;
  }, []);

  // Check if token is available
  const hasToken = useCallback(() => {
    return IS_DEVELOPMENT || !!turnstileRef.current;
  }, []);

  return {
    turnstileContainer,
    getToken,
    hasToken,
    resetTurnstile,
    isDevelopment: IS_DEVELOPMENT,
  };
};